import { Component, ElementRef, Input, QueryList, ViewChild, ViewChildren, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { TimeService } from '@services/time-service';
import { ReportFeedbackSheetComponent }
  from '@shared/bottom-sheet-layouts/report-feedback-sheet/report-feedback-sheet.component';
import { Catalog } from '@cureskin/api-client/src/server-api';
import { FollowUpReport } from '../app-constant-types';
import { AppConfig } from '../app.config';
import { UserAllocatedDoctorService } from '@services/user-allocated-doctor-service/user-allocated-doctor.service';
import { ExperimentService } from '@services/experiment';
import { CartService } from '@services/cart-service/cart.service';

/** Catalog.extraImagesWithTags tag whose image is shown on the added-product cards. */
const REGIMEN_SLIDER_IMAGE_TAG = 'regimenSlider';

@Component({
  selector: 'user-followup-report',
  templateUrl: './user-followup-report.html',
  styleUrls: ['./user-followup-report.scss'],
  standalone: false,
})

export class UserFollowupReportComponent implements OnInit {
  loading: boolean = true;
  username: string;
  user: any;
  instantCheckups: Array<any> = [];
  hideTitle: boolean = false;
  followupReport: any;
  report: FollowUpReport = { imageConfig: { after: {}, before: {} } };
  comparisonSliders: any[];
  animateProgressBar: boolean = false;
  throttle: boolean;
  interval: any;
  progressBarStages: any[];
  reportStagesLanguageStringId: string[] = Object.values(this.appConfig.Shared.FollowUpReport.Stages);
  isInternalUser: boolean;
  isHelpfull: boolean;
  afterSaveFeedback: boolean;
  feedback: any;
  /** Feedback question keys that already have a stored free-text ("No") message. */
  private readonly feedbackMessageKeys = new Set<string>();
  showFeedbackQuestions: boolean;
  iAmUselessVariable: boolean = false;
  doctorInfo: { name: string; imageUrl: string };
  followUpCount: number;
  addedProducts: Catalog[] = [];
  removedProducts: Catalog[] = [];
  instructionChanged: Catalog[] = [];
  improvementStages: string[] = [];
  /** followUp_report_v2 experiment — when false the original report UI is shown. */
  isReportV2: boolean = false;
  /** Guards against a double-tap on Buy Now adding the same products twice. */
  isBuyNowPending: boolean = false;
  /** Bar fill %, indexed by treatment-progress score 0–3 (worst → best). */
  private readonly improvementBarWidths: number[] = [17, 44, 68, 90];

  @ViewChild('audio', { static: false }) audio: ElementRef;
  // eslint-disable-next-line new-cap
  @ViewChildren('scrollContainer') scrollContainer: QueryList<ElementRef>;
  // eslint-disable-next-line new-cap
  @ViewChildren('progressBar') progressBar: QueryList<ElementRef>;
  // eslint-disable-next-line new-cap
  @ViewChildren('feedbackSection') feedbackSection: QueryList<ElementRef>;
  @Input('assistantId') assistantId: any;

  constructor(private conn: ConnectionService,
    private readonly broadcast: BroadcastService,
    public readonly route: ActivatedRoute,
    public readonly appConfig: AppConfig,
    private readonly eventLogger: EventLoggerService,
    private readonly timeService: TimeService,
    private readonly bottomSheet: MatBottomSheet,
    private readonly doctorService: UserAllocatedDoctorService,
    private readonly experimentService: ExperimentService,
    private readonly cartService: CartService) {
  }

  async ngOnInit(): Promise<any> {
    this.hideTitle = !!this.assistantId;
    this.isInternalUser = this.conn.isInternalUser();
    this.user = await this.conn.getActingUser();
    this.username = this.user?.get('username');
    await this.loadExperiments();
    await this.loadDoctorInfo();
    try {
      await this.loadDataForV2();
    } catch (error) {
      this.showError(error.message);
      this.broadcast.broadcast('NAVIGATION_BACK');
    }
    this.loading = false;
    if (!this.report.trackingDetails) {
      this.report.trackingDetails = {};
    }
  }

  /**
   * v2 - In version 2, the data is store in FollowupReport table.
   * so params.id is followup report id. We fetch the data and process it.
   */
  async loadDataForV2(): Promise<any> {
    const followUpReportId = this.route.snapshot.params.id;
    this.followupReport = await this.conn.fetchFollowUpReportById(followUpReportId);
    this.followUpCount = this.followupReport.get('followUp').get('followUpCount');
    this.report = JSON.parse(JSON.stringify(this.followupReport));
    this.feedback = this.report.feedback || {};
    // Seed keys that already have a stored "No" message so re-tapping "No" won't reopen the sheet.
    Object.keys(this.followupReport.get('feedbackMessages') || {}).forEach((key: string): void => {
      this.feedbackMessageKeys.add(key);
    });
    if (!this.isFeedbackComplete()
      && this.timeService.differenceInDays(new Date(), new Date(this.report.createdAt)) <= 7) {
      this.showFeedbackQuestions = true;
    }
    if (this.report.secondaryComparisons) {
      this.comparisonSliders = [this.followupReport, ...this.report.secondaryComparisons];
    } else {
      this.comparisonSliders = [this.followupReport];
    }
    await this.fetchProgressBarStageStrings();
    // v2 "Improvement Observed" uses a 4-point scale (score 0–3): Worsened, No Change, Good,
    // Excellent — Satisfactory is dropped. v1's <progress-bar> keeps the full 5-stage list.
    this.improvementStages = (this.progressBarStages || [])
      .filter((stage: any): boolean => stage.id !== this.appConfig.Shared.FollowUpReport.Stages.SATISFACTORY)
      .map((stage: any): string => this.conn.getValueOfLanguageString(stage));
    if (this.isReportV2) {
      await this.loadTreatmentUpdates(this.followupReport.id);
    }
    const interval = setInterval((): void => {
      if (this.scrollContainer.first) {
        this.scrollContainer.first.nativeElement.addEventListener('scroll', this.scrollListener());
        clearInterval(interval);
      }
    }, 150);
  }

  /**
   * These are language strings of progress bar stage.
   * eg: Excellent, Good, Bad, ...
   * we fetch the string by id stored in shared file and sort to get the result in same order.
   */
  async fetchProgressBarStageStrings(): Promise<void> {
    if (!this.report.isImageOnlyReport) {
      const stagesLanguageId = this.reportStagesLanguageStringId;
      const stages = await this.conn.getLanguageStringsById(stagesLanguageId);
      const stagesSorted = [];
      stagesLanguageId.forEach((id: string): void => {
        stages.forEach((each: any): void => {
          if (each.id === id) stagesSorted.push(each);
        });
      });
      this.progressBarStages = stagesSorted;
    }
  }

  /**
   * Resolves the followUp_report_v2 experiment. When inactive (or on error) the
   * original report UI is rendered unchanged.
   */
  async loadExperiments(): Promise<void> {
    await this.experimentService.experimentsLoaded;
    this.isReportV2 = this.experimentService.isFollowUpReportV2Enabled();
  }

  /**
   * Loads the regimen product changes for this report (products added / removed / instruction
   * changed), used by the "Product Update in Your Treatment" and "Products Added" sections. Fails
   * soft so the sections simply hide if the endpoint is unavailable.
   */
  async loadTreatmentUpdates(followUpReportId: string): Promise<void> {
    if (!followUpReportId) return;
    try {
      const updates = await this.conn.getFollowUpReportTreatmentUpdates(followUpReportId);
      this.addedProducts = updates.addedProducts ?? [];
      this.removedProducts = updates.removedProducts ?? [];
      this.instructionChanged = updates.instructionChanged ?? [];
    } catch {
      this.addedProducts = [];
      this.removedProducts = [];
      this.instructionChanged = [];
    }
  }

  /**
   * Image for an added-product card: the highest-priority `regimenSlider` entry from the catalog's
   * extraImagesWithTags, then the standard product-card fallback chain used across the app
   * (rebrandedImageWithBackground → productUnboxedImage → images).
   */
  addedProductImage(catalog: Catalog): string {
    const sliderImages = (catalog?.extraImagesWithTags || [])
      .filter((image): boolean => image.tag === REGIMEN_SLIDER_IMAGE_TAG);
    // Seeded with the first match, so `highest` is always defined once the callback runs; an empty
    // list short-circuits to an undefined seed without ever calling it.
    const sliderImage = sliderImages
      .reduce((highest, image) => ((image.priority ?? 0) > (highest.priority ?? 0) ? image : highest),
        sliderImages[0]);
    return sliderImage?.imageLink
      || catalog?.rebrandedImageWithBackground?.[0]
      || catalog?.productUnboxedImage?.[0]
      || catalog?.images?.[0]
      || '';
  }

  /** Localized status word (Worsened … Excellent) for an Improvement Observed row. */
  improvementStatusLabel(score: number): string {
    return this.improvementStages[this.clampScore(score)] ?? '';
  }

  /** Green for improved scores (2–3), orange for Worsened/No Change (0–1). */
  improvementBarColorClass(score: number): string {
    return this.clampScore(score) >= 2 ? 'tw-bg-green-400' : 'tw-bg-orange-400';
  }

  /** Bar fill percentage for an Improvement Observed row. */
  improvementBarWidth(score: number): number {
    return this.improvementBarWidths[this.clampScore(score)];
  }

  private clampScore(score: number): number {
    const index = Math.round(score);
    if (index < 0) return 0;
    if (index >= this.improvementBarWidths.length) return this.improvementBarWidths.length - 1;
    return index;
  }

  /**
   * Adds any of the report's newly-added products that are not already in the cart (one quantity
   * each, existing lines untouched) before sending the user to the cart.
   */
  async buyNow(): Promise<void> {
    if (this.isBuyNowPending) return;
    this.isBuyNowPending = true;
    this.eventLogger.trackEvent('followUpReport_buyNow', { followupReportId: this.report.objectId, pageName: 'checkup' });
    await this.cartService.ensureProductsInCart(this.addedProducts, 'followUpReport');
    this.isBuyNowPending = false;
    this.conn.navigateToURL('/cart');
  }

  /**
   * Opens the feedback bottom-sheet after a user answers a question "No",
   * persisting their free-text message against that question key. When this "No"
   * was the answer that completed the feedback, the thank-you is held back until
   * the sheet goes away — whether the user submits a message or just closes it —
   * so the user is never left on the questions with nowhere to go.
   */
  openFeedbackSheet(key: string, markCompleteOnClose: boolean = false): void {
    const sheetRef = this.bottomSheet.open(ReportFeedbackSheetComponent, {
      panelClass: 'report-feedback-sheet-panel',
      data: {
        onSubmit: async (message: string): Promise<void> => {
          await this.conn.saveFollowUpReportFeedbackMessage(this.report.objectId, key, message);
          this.feedbackMessageKeys.add(key);
        },
      },
    });
    if (!markCompleteOnClose) return;
    sheetRef.afterDismissed().subscribe((): void => {
      this.afterSaveFeedback = true;
    });
  }

  /** True once every feedback question required for this follow-up has been answered. */
  private isFeedbackComplete(): boolean {
    const baseComplete = (this.feedback?.isReportSatisfied !== undefined)
      && (this.feedback?.isVoiceNoteUnderstandable !== undefined);
    return this.followUpCount === 1
      ? baseComplete
      : baseComplete && (this.feedback?.doubtsCleared !== undefined);
  }

  /**
   * Listens for the scroll and when the treatment progress bar section is reached, it sets the flag to animate the progress.
   * Then it removes the listener. The animations lifetime is only once.
   */
  scrollListener(): (event: any) => any {
    return (event: any): void => {
      if (this.throttle && this.report.trackingDetails.scrollTillBottom) return;
      this.interval = setTimeout((): boolean => this.throttle = false, 150);
      this.throttle = true;
      const scrollContainer = event.target;
      const progressBarPosition = scrollContainer.scrollTop + scrollContainer.clientHeight;
      if (progressBarPosition >= this.progressBar?.first?.nativeElement.offsetTop) {
        this.animateProgressBar = true;
        clearInterval(this.interval);
      }
      if (progressBarPosition >= this.feedbackSection?.first?.nativeElement.offsetTop) {
        if (!this.report.trackingDetails?.scrollTillBottom) {
          this.report.trackingDetails.scrollTillBottom = true;
          this.eventLogger.cleverTapEvent('scrollTillBottom', JSON.stringify({ followupReportId: this.report.objectId,
            pageName: 'checkup' }));
          this.conn.trackVoiceNotesAndScrollEvents({ objectId: this.report.objectId,
            eventName: 'scrollTillBottom',
            trackingTable: 'followUpReport' });
        }
        event.target.removeEventListener('scroll', this.scrollListener());
      }
    };
  }

  findTypeOfReport(): string {
    let type: string;
    if ([this.appConfig.Shared.InstantCheckup.Type.FULL_FACE,
      this.appConfig.Shared.InstantCheckup.Type.SIDE_FACE,
      this.appConfig.Shared.InstantCheckup.Type.LEFT_SIDE_FACE,
      this.appConfig.Shared.InstantCheckup.Type.RIGHT_SIDE_FACE,
      this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE].includes(this.instantCheckups[0].type)) {
      type = this.appConfig.Shared.Regimen.Class.FACE;
    } else if ([this.appConfig.Shared.InstantCheckup.Type.HAIR,
      this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT,
      this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP].includes(this.instantCheckups[0].type)) {
      type = this.appConfig.Shared.Regimen.Class.HAIR;
    } else type = this.appConfig.Shared.Regimen.Class.BODY;
    return type;
  }

  openConsentForm(): void {
    this.conn.navigateToURL(`/chatV2/${this.report.supportTicket?.objectId}?type=supportTicket&username=${this.username}`
    + `&reportId=${this.report.objectId}`);
  }

  viewRegimen(): void {
    this.conn.navigateToURL(`/user?tab=regimen&class=${this.report.type}&username=${this.username}`);
  }

  async onAudioPlay(): Promise<void> {
    if (this.report.trackingDetails?.audioPlayed) return;
    this.report.trackingDetails.audioPlayed = true;
    const voiceNote = this.report.objectId ? this.appConfig.Shared.questionId.checkupVoiceNote : 'undefined';
    this.eventLogger.cleverTapEvent('audioPlayed', JSON.stringify({ followupReportId: this.report.objectId, pageName: 'checkup', voiceNote }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    await this.conn.trackVoiceNotesAndScrollEvents({ objectId: this.report.objectId,
      eventName: 'audioPlayed',
      trackingTable: 'followUpReport' });
  }

  async onAudioPlayEnd(): Promise<void> {
    if (this.report?.trackingDetails?.audioFinished) return;
    this.report.trackingDetails.audioFinished = true;
    this.eventLogger.cleverTapEvent('audioFinished', JSON.stringify({ followupReportId: this.report.objectId, pageName: 'checkup' }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    await this.conn.trackVoiceNotesAndScrollEvents({ objectId: this.report.objectId,
      eventName: 'audioFinished',
      trackingTable: 'followUpReport' });
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  showError(message: any): void {
    this.broadcast.broadcast('NOTIFY', { message });
  }

  async updateFeedback(isHelpfull: boolean): Promise<void> {
    if (this.conn.isInternalUser()) return;
    this.isHelpfull = isHelpfull;
    this.followupReport.set('feedback', { feedback: isHelpfull });
    await this.followupReport.save();
    this.afterSaveFeedback = true;
  }

  async saveFeedback(key: string, value: boolean): Promise<void> {
    if (this.conn.isInternalUser()) return;
    this.feedback[key] = value;
    this.followupReport.set('feedback', this.feedback);
    try {
      await this.followupReport.save();
    } catch (error) {
      // Save failed — roll the answer back so the user can pick again.
      delete this.feedback[key];
      this.followupReport.set('feedback', this.feedback);
      this.broadcast.broadcast('NOTIFY', { message: error.message });
      return;
    }
    // Switching a question back to "Yes" clears any "No" message it had (empty message deletes it).
    if (value === true && this.feedbackMessageKeys.has(key)) {
      this.feedbackMessageKeys.delete(key);
      await this.conn.saveFollowUpReportFeedbackMessage(this.report.objectId, key, '');
    }
    const feedbackComplete = this.isFeedbackComplete();
    // A "No" opens the free-text sheet — only in v2, and only if no message is stored yet for this
    // question. Tapping "No" again after a message keeps it closed until the user switches to Yes.
    if (value === false && this.isReportV2 && !this.feedbackMessageKeys.has(key)) {
      // When this "No" completed the feedback, defer the thank-you to the sheet's submit.
      this.openFeedbackSheet(key, feedbackComplete);
      return;
    }
    if (feedbackComplete) this.afterSaveFeedback = true;
  }

  async trackEvent(action: any): Promise<void> {
    switch (action) {
      case 'play': {
        await this.onAudioPlay();
        break;
      }
      case 'completed': {
        await this.onAudioPlayEnd();
        break;
      }
      default:
    }
  }

  async loadDoctorInfo(): Promise<void> {
    const doctor = await this.doctorService.getAllocatedDoctor(true);
    this.doctorInfo = { name: doctor.DoctorDisplayName, imageUrl: doctor.doctorDisplayImage };
  }
}
